import { useTranslation } from 'react-i18next';
import type { i18n as I18nType } from 'i18next';
import { getDefaultTaxYear } from '../features/flow/utils';
import type { SessionScreen } from '../features/flow/session';
import type { ScreenQuestion } from '../features/flow/types';

/** Resolve `{{tax_year}}` for i18next — uses session answers, then calendar year. */
function taxYearInterpolation(answers?: Record<string, unknown> | null): { tax_year: string } {
  const raw = answers?.tax_year ?? answers?.year;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    return { tax_year: String(raw).trim() };
  }
  return { tax_year: String(getDefaultTaxYear()) };
}

/**
 * When `en/screens.json` defines `paragraphs.0` … `paragraphs.N` (or the same for
 * `bullet_points`) as a replacement block, use only those strings so stale extra
 * items from FIGMA_ALL_SCREENS.json are not shown.
 */
function translateIndexedScreenStrings(
  sid: string,
  field: 'paragraphs' | 'bullet_points',
  raw: string[],
  t: (key: string, opts: Record<string, unknown>) => string,
  i18n: I18nType,
  interp: { tax_year: string }
): string[] {
  if (raw.length === 0) return [];
  const ns = 'screens';
  const prefix = `${sid}.${field}`;
  if (!i18n.exists(`${prefix}.0`, { ns })) {
    return raw.map((p, idx) => t(`${prefix}.${idx}`, { defaultValue: p, ...interp }));
  }
  const out: string[] = [];
  let i = 0;
  while (i18n.exists(`${prefix}.${i}`, { ns })) {
    out.push(t(`${prefix}.${i}`, { defaultValue: raw[i] ?? '', ...interp }));
    i += 1;
  }
  return out;
}

/**
 * Translates a raw SessionScreen (served in English by the backend) into the
 * user's selected language using the `screens` i18next namespace.
 *
 * Every translatable field is passed through `t(key, { defaultValue: english })`
 * so the English source text is always the fallback — no raw translation keys
 * are ever visible in the UI.
 *
 * `answers` supplies `tax_year` so locale strings with `{{tax_year}}` interpolate
 * (must match backend `_interpolate_tax_year` / Postman behavior).
 *
 * Returns `null` when `screen` is `null` (avoids conditional hook calls at
 * call sites).
 */
export function useTranslatedScreen(
  screen: SessionScreen | null,
  answers?: Record<string, unknown> | null
): SessionScreen | null {
  const { t, i18n } = useTranslation('screens');

  const interp = taxYearInterpolation(answers);

  if (!screen) return null;

  const sid = screen.screen_id;

  const translatedQuestions = (screen.questions as ScreenQuestion[]).map((q) => {
    const qid = q.id;
    const translated: ScreenQuestion = { ...q };

    if (q.question_text) {
      translated.question_text = t(`${sid}.questions.${qid}.question_text`, {
        defaultValue: q.question_text,
        ...interp,
      });
    }

    if (q.placeholder) {
      translated.placeholder = t(`${sid}.questions.${qid}.placeholder`, {
        defaultValue: q.placeholder,
        ...interp,
      });
    }

    if (q.help_text) {
      translated.help_text = t(`${sid}.questions.${qid}.help_text`, {
        defaultValue: q.help_text,
        ...interp,
      });
    }

    if (Array.isArray(q.options) && q.options.length > 0) {
      translated.options = q.options.map((opt, idx) =>
        t(`${sid}.questions.${qid}.options.${idx}`, { defaultValue: opt, ...interp })
      );
    }

    return translated;
  });

  return {
    ...screen,
    title: screen.title
      ? t(`${sid}.title`, { defaultValue: screen.title, ...interp })
      : screen.title,
    subtitle: screen.subtitle
      ? t(`${sid}.subtitle`, { defaultValue: screen.subtitle, ...interp })
      : screen.subtitle,
    description: screen.description
      ? t(`${sid}.description`, { defaultValue: screen.description, ...interp })
      : screen.description,
    paragraphs: translateIndexedScreenStrings(sid, 'paragraphs', screen.paragraphs, t, i18n, interp),
    bullet_points: translateIndexedScreenStrings(
      sid,
      'bullet_points',
      screen.bullet_points,
      t,
      i18n,
      interp
    ),
    buttons: screen.buttons.map((btn, idx) =>
      t(`${sid}.buttons.${idx}`, { defaultValue: btn, ...interp })
    ),
    questions: translatedQuestions,
  };
}